import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from 'react-router-dom';
import {
    getDataSourcesForCollection,
    getAgentsForTenant,
    getCollectionDetails,
    updateDataSource,
    deleteDataSource,
    deleteAgent
} from '../services/apiClient';
import { getAuthHeaders } from '../utils/getAuthHeaders';
import { logger } from '../utils/logger';

/**
 * Custom hook to manage fetching and updating sidebar data (Data Sources and Agents).
 * 
 * @returns {object} - An object containing:
 *   - dataSources: Array of data source objects.
 *   - agents: Array of agent objects.
 *   - error: Error message string if fetching fails, otherwise null.
 *   - isLoading: Boolean indicating if initial data is loading.
 *   - handleDataSourceUpdate: Function to manually trigger a refresh of data sources.
 *   - handleAgentsUpdate: Function to manually trigger a refresh of agents.
 *   - editingDataSource: The data source object currently being edited, or null.
 *   - showEditDataSourceModal: Boolean indicating if the edit modal should be shown.
 *   - handleEditDataSource: Function to initiate editing a data source.
 *   - handleUpdateDataSourceSubmit: Function to submit the updated data source.
 *   - handleCloseEditModal: Function to close the edit modal.
 *   - deletingDataSourceId: The ID of the data source pending deletion confirmation.
 *   - showDeleteConfirm: Boolean indicating if the delete confirmation dialog should be shown.
 *   - handleDeleteDataSourceClick: Function to initiate deleting a data source.
 *   - handleConfirmDelete: Function to confirm and execute deletion.
 *   - handleCancelDelete: Function to cancel DataSource deletion.
 *   - deletingAgentId: The ID of the agent pending deletion confirmation.
 *   - showAgentDeleteConfirm: Boolean indicating if the agent delete confirmation dialog should be shown.
 *   - handleDeleteAgentClick: Function to initiate deleting an agent.
 *   - handleConfirmAgentDelete: Function to confirm and execute agent deletion.
 *   - handleCancelAgentDelete: Function to cancel agent deletion.
 */
export const useSidebarData = () => {
    const [dataSources, setDataSources] = useState([]);
    const [agents, setAgents] = useState([]);
    const [collection, setCollection] = useState(null);
    const [error, setError] = useState(null);
    const [editError, setEditError] = useState(null); // Specific error for DS edit operations
    const [deleteError, setDeleteError] = useState(null); // Specific error for DS delete operations
    const [agentDeleteError, setAgentDeleteError] = useState(null); // Specific error for agent delete
    const [isLoading, setIsLoading] = useState(true); // Track initial loading state
    const [editingDataSource, setEditingDataSource] = useState(null); // State for the DS being edited
    const [showEditDataSourceModal, setShowEditDataSourceModal] = useState(false); // State for modal visibility
    const [deletingDataSourceId, setDeletingDataSourceId] = useState(null); // State for DS pending deletion
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // State for DS delete confirmation dialog
    const [deletingAgentId, setDeletingAgentId] = useState(null); // State for Agent pending deletion
    const [showAgentDeleteConfirm, setShowAgentDeleteConfirm] = useState(false); // State for Agent delete confirmation

    const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
    const { collectionId } = useParams();

    const fetchData = useCallback(async () => {
        if (isAuthenticated && user && collectionId) {
            setIsLoading(true); // Set loading true when fetching starts
            // Removed redundant setError(null)
            setError(null); // Clear previous errors
            setEditError(null);
            setDeleteError(null);
            setAgentDeleteError(null); // Clear agent delete errors too
            try {
                const tenantId = user['https://tenxer.ai/tenant_id'];
                const token = await getAccessTokenSilently();
                if (!token) throw new Error("Failed to get access token for sidebar data");

                const headers = getAuthHeaders(token); // Create headers object once

                // Fetch Collection Details (including auth headers)
                logger.debug("Fetching Collection details for sidebar...");
                const fetchedCollection = await getCollectionDetails(tenantId, collectionId, token);
                setCollection(fetchedCollection || null);

                // Fetch DataSources
                logger.debug("Fetching DataSources for sidebar...");
                const fetchedDataSources = await getDataSourcesForCollection(tenantId, collectionId, token);
                setDataSources(fetchedDataSources || []);

                // Fetch Agents
                logger.debug("Fetching Agents for sidebar...");
                const fetchedAgents = await getAgentsForTenant(tenantId, headers);
                setAgents(fetchedAgents || []);

            } catch (err) {
                logger.error('Error fetching sidebar data:', err);
                setError(err.message || 'Failed to load collection data or agents.');
                setCollection(null); // Clear data on error
                setDataSources([]);
                setAgents([]);
            } finally {
                setIsLoading(false); // Set loading false when fetching completes or fails
            }
        } else {
             // If not authenticated or missing info, set loading false
             setIsLoading(false);
             setCollection(null);
             setDataSources([]);
             setAgents([]);
        }
    }, [isAuthenticated, user, collectionId, getAccessTokenSilently]); // Dependencies for fetching

    // Initial data fetch
    // Initial data fetch
    useEffect(() => {
        fetchData();
    }, [fetchData]); // Run fetch data when the callback changes

    // Callback to refresh data sources
    // Callback to refresh data sources (used after add/update/delete)
    const handleDataSourceUpdate = useCallback(async () => {
        if (isAuthenticated && user && collectionId) {
            // Optionally add a loading indicator specific to refresh
            try {
                setError(null); // Clear previous errors before refetch
                setEditError(null);
                setDeleteError(null);
                setAgentDeleteError(null);
                const tenantId = user['https://tenxer.ai/tenant_id'];
                const token = await getAccessTokenSilently();
                if (!token) throw new Error("Failed to get access token for data source update");

                logger.debug("Refreshing DataSources...");
                const fetchedDataSources = await getDataSourcesForCollection(tenantId, collectionId, token);
                setDataSources(fetchedDataSources || []);
            } catch (err) {
                logger.error("Error refreshing data sources:", err);
                // Set general error, as this is a refresh, not a specific edit action
                setError(err.message || "Failed to refresh data sources after update.");
            }
        }
    }, [isAuthenticated, user, collectionId, getAccessTokenSilently]);

    // Callback to refresh agents
    // Callback to refresh agents
    const handleAgentsUpdate = useCallback(async () => {
        if (isAuthenticated && user) {
            try {
                setError(null); // Clear previous errors before refetch
                setEditError(null);
                setDeleteError(null);
                setAgentDeleteError(null);
                const tenantId = user['https://tenxer.ai/tenant_id'];
                const token = await getAccessTokenSilently();
                if (!token) throw new Error("Failed to get access token for agent update");
                const headers = getAuthHeaders(token); // Create headers object

                logger.debug("Refreshing Agents...");
                const fetchedAgents = await getAgentsForTenant(tenantId, headers); // Pass headers
                setAgents(fetchedAgents || []);
            } catch (err) {
                logger.error("Error refreshing agents:", err);
                setError(err.message || "Failed to refresh agents after update.");
            }
        }
    }, [isAuthenticated, user, getAccessTokenSilently]);

    // --- Edit DataSource Handlers ---

    const handleEditDataSource = useCallback((dataSource) => {
        logger.debug("Editing DataSource:", dataSource);
        setEditError(null); // Clear previous edit errors
        // Ensure we have the correct ID field (DataSourceId or data_source_id)
        const id = dataSource.DataSourceId || dataSource.data_source_id;
        if (!id) {
            logger.error("DataSource is missing an ID:", dataSource);
            setEditError("Cannot edit data source: Missing ID.");
            return;
        }
        setEditingDataSource({ ...dataSource, id }); // Store the DS with a consistent ID field
        setShowEditDataSourceModal(true);
    }, []);

    const handleCloseEditModal = useCallback(() => {
        setShowEditDataSourceModal(false);
        setEditingDataSource(null);
        setEditError(null); // Clear edit errors on close
    }, []);

    const handleUpdateDataSourceSubmit = useCallback(async (updatedData) => {
        if (!editingDataSource || !editingDataSource.id) {
            logger.error("No data source selected for update.");
            setEditError("No data source selected for update.");
            return;
        }
        if (!isAuthenticated || !user || !collectionId) {
            setEditError("Authentication or context missing. Cannot update.");
            return;
        }

        setEditError(null); // Clear previous errors
        // Consider adding a loading state specific to the edit modal submission

        try {
            const tenantId = user['https://tenxer.ai/tenant_id'];
            const token = await getAccessTokenSilently();
            if (!token) throw new Error("Failed to get access token for update");

            // The backend PUT expects the *entire* updated object.
            // Ensure the payload includes the ID and type.
            const payload = {
                ...updatedData, // Contains the form field values
                DataSourceId: editingDataSource.id, // Ensure ID is present
                DataSourceType: editingDataSource.DataSourceType || editingDataSource.data_source_type // Ensure type is present
            };

            logger.debug("Submitting update for DataSource:", editingDataSource.id, "Payload:", payload);

            await updateDataSource(tenantId, collectionId, editingDataSource.id, payload, token);

            logger.debug("DataSource updated successfully.");
            handleCloseEditModal(); // Close modal on success
            await handleDataSourceUpdate(); // Refresh the list

        } catch (err) {
            logger.error("Error updating data source:", err);
            setEditError(err.message || "Failed to update data source.");
            // Keep the modal open on error so the user can see the message
        } finally {
            // Turn off any edit-specific loading indicator here
        }
    }, [
        editingDataSource,
        isAuthenticated,
        user,
        collectionId,
        getAccessTokenSilently,
        handleDataSourceUpdate,
        handleCloseEditModal
    ]);

    // --- Delete DataSource Handlers ---

    const handleDeleteDataSourceClick = useCallback((dataSourceId) => {
        if (!dataSourceId) {
            logger.error("No DataSource ID provided for deletion.");
            return;
        }
        logger.debug("Requesting delete confirmation for DataSource:", dataSourceId);
        setDeleteError(null); // Clear previous delete errors
        setDeletingDataSourceId(dataSourceId);
        setShowDeleteConfirm(true);
    }, []);

    const handleCancelDelete = useCallback(() => {
        setShowDeleteConfirm(false);
        setDeletingDataSourceId(null);
        setDeleteError(null);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!deletingDataSourceId) {
            logger.error("No data source ID set for deletion confirmation.");
            setDeleteError("Deletion failed: No data source selected.");
            return;
        }
        if (!isAuthenticated || !user || !collectionId) {
            setDeleteError("Authentication or context missing. Cannot delete.");
            return;
        }

        setDeleteError(null);
        // Consider adding a loading state specific to deletion

        try {
            const tenantId = user['https://tenxer.ai/tenant_id'];
            const token = await getAccessTokenSilently();
            if (!token) throw new Error("Failed to get access token for delete");

            logger.debug("Confirming delete for DataSource:", deletingDataSourceId);

            await deleteDataSource(tenantId, collectionId, deletingDataSourceId, token);

            logger.debug("DataSource deleted successfully.");
            handleCancelDelete(); // Close confirmation on success
            await handleDataSourceUpdate(); // Refresh the list

        } catch (err) {
            logger.error("Error deleting data source:", err);
            setDeleteError(err.message || "Failed to delete data source.");
            // Keep the confirmation dialog open on error? Or close it? Decide based on UX preference.
            // For now, let's keep it open so the error is visible.
        } finally {
            // Turn off any delete-specific loading indicator here
        }
    }, [
        deletingDataSourceId,
        isAuthenticated,
        user,
        collectionId,
        getAccessTokenSilently,
        handleDataSourceUpdate,
        handleCancelDelete // Dependency for closing dialog
    ]);

    // --- Agent Delete Handlers ---

    const handleDeleteAgentClick = useCallback((agentId) => {
        if (!agentId) {
            logger.error("No Agent ID provided for deletion.");
            return;
        }
        logger.debug("Requesting delete confirmation for Agent:", agentId);
        setAgentDeleteError(null); // Clear previous agent delete errors
        setDeletingAgentId(agentId);
        setShowAgentDeleteConfirm(true);
    }, []);

    const handleCancelAgentDelete = useCallback(() => {
        setShowAgentDeleteConfirm(false);
        setDeletingAgentId(null);
        setAgentDeleteError(null);
    }, []);

    const handleConfirmAgentDelete = useCallback(async () => {
        if (!deletingAgentId) {
            logger.error("No agent ID set for deletion confirmation.");
            setAgentDeleteError("Deletion failed: No agent selected.");
            return;
        }
        if (!isAuthenticated || !user) { // No collectionId needed for agent delete
            setAgentDeleteError("Authentication or context missing. Cannot delete agent.");
            return;
        }

        setAgentDeleteError(null);
        // Consider adding a loading state specific to agent deletion

        try {
            const tenantId = user['https://tenxer.ai/tenant_id'];
            const token = await getAccessTokenSilently();
            if (!token) throw new Error("Failed to get access token for agent delete");
            const headers = getAuthHeaders(token);

            logger.debug("Confirming delete for Agent:", deletingAgentId);

            await deleteAgent(tenantId, deletingAgentId, headers);

            logger.debug("Agent deleted successfully.");
            handleCancelAgentDelete(); // Close confirmation on success
            await handleAgentsUpdate(); // Refresh the agent list

        } catch (err) {
            logger.error("Error deleting agent:", err);
            setAgentDeleteError(err.message || "Failed to delete agent.");
            // Keep the confirmation dialog open on error
        } finally {
            // Turn off any agent-delete-specific loading indicator here
        }
    }, [
        deletingAgentId,
        isAuthenticated,
        user,
        getAccessTokenSilently,
        handleAgentsUpdate,
        handleCancelAgentDelete // Dependency for closing dialog
    ]);

    // --- Return Values ---

    return {
        dataSources,
        agents,
        collection,
        error, // General fetch error
        editError, // Specific error for DS edit operations
        deleteError, // Specific error for DS delete operations
        agentDeleteError, // Specific error for agent delete operations
        isLoading, // Expose loading state
        handleDataSourceUpdate, // Refresh function
        handleAgentsUpdate, // Refresh function
        // DS Edit state and handlers
        editingDataSource,
        showEditDataSourceModal,
        handleEditDataSource,
        handleUpdateDataSourceSubmit,
        handleCloseEditModal,
        // DS Delete state and handlers
        deletingDataSourceId,
        showDeleteConfirm,
        handleDeleteDataSourceClick,
        handleConfirmDelete,
        handleCancelDelete,
        // Agent Delete state and handlers
        deletingAgentId,
        showAgentDeleteConfirm,
        handleDeleteAgentClick,
        handleConfirmAgentDelete,
        handleCancelAgentDelete,
    };
};